import React, { useState, useEffect } from "react";
import { getPublicationsAdmin, createPublication, updatePublication, deletePublication } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const PublicationsManager = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [formData, setFormData] = useState({
    en: { title: "", publication_date_text: "", publisher: "", document_type: "", contributors: "", notes: "", link_text: "" },
    vi: { title: "", publication_date_text: "", publisher: "", document_type: "", contributors: "", notes: "", link_text: "" },
    link_url: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    setLoading(true);
    try {
      const data = await getPublicationsAdmin();
      setPublications(data);
    } catch (err) {
      setToast({ message: "Failed to load publications", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        link_url: formData.link_url,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updatePublication(editingId, payload);
      } else {
        await createPublication(payload);
      }
      fetchPublications();
      resetForm();
      setToast({ message: "Publication saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save publication", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this publication?")) {
      try {
        await deletePublication(id);
        fetchPublications();
        setToast({ message: "Publication deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete publication", type: "error" });
      }
    }
  };

  const handleEdit = (publication) => {
    setFormData({
      en: {
        title: publication.translations.en?.title || "",
        publication_date_text: publication.translations.en?.publication_date_text || "",
        publisher: publication.translations.en?.publisher || "",
        document_type: publication.translations.en?.document_type || "",
        contributors: publication.translations.en?.contributors || "",
        notes: publication.translations.en?.notes || "",
        link_text: publication.translations.en?.link_text || "",
      },
      vi: {
        title: publication.translations.vi?.title || "",
        publication_date_text: publication.translations.vi?.publication_date_text || "",
        publisher: publication.translations.vi?.publisher || "",
        document_type: publication.translations.vi?.document_type || "",
        contributors: publication.translations.vi?.contributors || "",
        notes: publication.translations.vi?.notes || "",
        link_text: publication.translations.vi?.link_text || "",
      },
      link_url: publication.link_url || "",
      sort_order: publication.sort_order,
      is_active: publication.is_active,
    });
    setEditingId(publication.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { title: "", publication_date_text: "", publisher: "", document_type: "", contributors: "", notes: "", link_text: "" },
      vi: { title: "", publication_date_text: "", publisher: "", document_type: "", contributors: "", notes: "", link_text: "" },
      link_url: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
  };

  if (loading) return <div className="admin-manager-loading">Loading publications...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Publication"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Publication" : "Create New Publication"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Title (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.title}
                    onChange={(e) => handleInputChange("en", "title", e.target.value)}
                    placeholder="English title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Publication Date (EN)</label>
                  <input
                    type="text"
                    value={formData.en.publication_date_text}
                    onChange={(e) => handleInputChange("en", "publication_date_text", e.target.value)}
                    placeholder="English publication date"
                  />
                </div>
                <div className="form-group">
                  <label>Publisher (EN)</label>
                  <input
                    type="text"
                    value={formData.en.publisher}
                    onChange={(e) => handleInputChange("en", "publisher", e.target.value)}
                    placeholder="English publisher"
                  />
                </div>
                <div className="form-group">
                  <label>Document Type (EN)</label>
                  <input
                    type="text"
                    value={formData.en.document_type}
                    onChange={(e) => handleInputChange("en", "document_type", e.target.value)}
                    placeholder="English document type"
                  />
                </div>
                <div className="form-group">
                  <label>Contributors (EN)</label>
                  <input
                    type="text"
                    value={formData.en.contributors}
                    onChange={(e) => handleInputChange("en", "contributors", e.target.value)}
                    placeholder="English contributors"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (EN)</label>
                  <textarea
                    value={formData.en.notes}
                    onChange={(e) => handleInputChange("en", "notes", e.target.value)}
                    placeholder="English notes"
                  />
                </div>
                <div className="form-group">
                  <label>Link Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.link_text}
                    onChange={(e) => handleInputChange("en", "link_text", e.target.value)}
                    placeholder="English link text"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Title (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.title}
                    onChange={(e) => handleInputChange("vi", "title", e.target.value)}
                    placeholder="Vietnamese title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Publication Date (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.publication_date_text}
                    onChange={(e) => handleInputChange("vi", "publication_date_text", e.target.value)}
                    placeholder="Vietnamese publication date"
                  />
                </div>
                <div className="form-group">
                  <label>Publisher (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.publisher}
                    onChange={(e) => handleInputChange("vi", "publisher", e.target.value)}
                    placeholder="Vietnamese publisher"
                  />
                </div>
                <div className="form-group">
                  <label>Document Type (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.document_type}
                    onChange={(e) => handleInputChange("vi", "document_type", e.target.value)}
                    placeholder="Vietnamese document type"
                  />
                </div>
                <div className="form-group">
                  <label>Contributors (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.contributors}
                    onChange={(e) => handleInputChange("vi", "contributors", e.target.value)}
                    placeholder="Vietnamese contributors"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (VI)</label>
                  <textarea
                    value={formData.vi.notes}
                    onChange={(e) => handleInputChange("vi", "notes", e.target.value)}
                    placeholder="Vietnamese notes"
                  />
                </div>
                <div className="form-group">
                  <label>Link Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.link_text}
                    onChange={(e) => handleInputChange("vi", "link_text", e.target.value)}
                    placeholder="Vietnamese link text"
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>General Information</h4>
              <div className="form-group">
                <label>Link URL</label>
                <input
                  type="text"
                  value={formData.link_url}
                  onChange={(e) => handleMetaChange("link_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Publication
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Title (EN)</th>
              <th>Publisher</th>
              <th>Type</th>
              <th>Date</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {publications.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                  No publications found
                </td>
              </tr>
            ) : (
              publications.map((publication, index) => (
                <tr key={publication.id}>
                  <td>{index + 1}</td>
                  <td>{publication.translations.en?.title || "N/A"}</td>
                  <td>{publication.translations.en?.publisher || "N/A"}</td>
                  <td>{publication.translations.en?.document_type || "N/A"}</td>
                  <td>{publication.translations.en?.publication_date_text || "N/A"}</td>
                  <td>{publication.is_active ? "Yes" : "No"}</td>
                  <td>{publication.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(publication)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(publication.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PublicationsManager;
